import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Play, RefreshCw, Check, X, AlertTriangle, Eye, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function GamblingModeration() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending_review");
  const [loading, setLoading] = useState(true);
  const [scanState, setScanState] = useState(null);
  const [triggering, setTriggering] = useState(false);
  const [selected, setSelected] = useState(null);
  const [processing, setProcessing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [listR, statusR] = await Promise.all([
        axios.get(`${API}/api/admin/gambling/moderation?status=${status}&limit=100`),
        axios.get(`${API}/api/admin/gambling/scan-status`),
      ]);
      setItems(listR.data.items || []);
      setScanState(statusR.data);
    } catch (e) {
      toast.error(`Błąd: ${e?.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  // Auto-refresh gdy scan running
  useEffect(() => {
    if (!scanState?.running) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [scanState?.running]);

  const triggerScan = async () => {
    if (!confirm("Uruchomić skan gambling posts? Sprawdzi wszystkie posty z gambling-notice + linki.")) return;
    setTriggering(true);
    try {
      const r = await axios.post(`${API}/api/admin/gambling/scan`, {});
      if (r.data?.error) toast.error(r.data.error);
      else toast.success(`Skan uruchomiony (job ${r.data.job_id?.slice(0,8)})`);
      await load();
    } catch (e) {
      toast.error(`Błąd triggeru: ${e?.response?.data?.detail || e.message}`);
    } finally { setTriggering(false); }
  };

  const openDetail = async (id) => {
    try {
      const r = await axios.get(`${API}/api/admin/gambling/moderation/${id}`);
      setSelected(r.data);
    } catch (e) { toast.error(`Błąd: ${e?.response?.data?.detail || e.message}`); }
  };

  const applyMod = async () => {
    if (!selected) return;
    if (!confirm("Zastosować zmiany do postu? (aktualizuje treść w bazie — portal aktualizujesz osobno)")) return;
    setProcessing(true);
    try {
      const r = await axios.post(`${API}/api/admin/gambling/moderation/${selected.id}/apply`,
                                  { publish_to_portal: false });
      if (r.data?.error) toast.error(r.data.error);
      else { toast.success("Zaktualizowano post w bazie"); setSelected(null); await load(); }
    } catch (e) { toast.error(`Błąd: ${e?.response?.data?.detail || e.message}`); }
    finally { setProcessing(false); }
  };

  const rejectMod = async () => {
    if (!selected) return;
    const reason = prompt("Powód odrzucenia (opcjonalny):") || "";
    setProcessing(true);
    try {
      const r = await axios.post(`${API}/api/admin/gambling/moderation/${selected.id}/reject`,
                                  { reason });
      if (r.data?.ok) { toast.success("Odrzucono"); setSelected(null); await load(); }
    } catch (e) { toast.error(`Błąd: ${e?.response?.data?.detail || e.message}`); }
    finally { setProcessing(false); }
  };

  const statusInfo = {
    pending_review: { label: "Do przeglądu", color: "amber" },
    applied: { label: "Zastosowane", color: "emerald" },
    rejected: { label: "Odrzucone", color: "gray" },
  };

  return (
    <div className="space-y-6 max-w-none">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-amber-500" />
            Gambling — moderacja
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Skanuje nasze publikacje (z markerem <code>gambling-notice</code>), weryfikuje linki,
            proponuje edukacyjną wersję dla dead/blocked. Ręczny trigger, ręczne zatwierdzanie.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Odśwież
          </Button>
          <Button size="sm" onClick={triggerScan} disabled={triggering || scanState?.running}
                  className="bg-amber-600 hover:bg-amber-700 gap-1">
            {(triggering || scanState?.running) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            {scanState?.running ? "Skan w toku…" : "Uruchom skan"}
          </Button>
        </div>
      </div>

      {/* Status ostatniego skanu */}
      {scanState?.last_history && (
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground mb-1">Ostatni skan</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><span className="text-muted-foreground">Sprawdzone:</span> <strong>{scanState.last_history.scanned}</strong></div>
              <div><span className="text-muted-foreground">Z dead linkami:</span> <strong className="text-red-600">{scanState.last_history.with_dead_links}</strong></div>
              <div><span className="text-muted-foreground">W kolejce:</span> <strong className="text-amber-600">{scanState.last_history.queued_for_review}</strong></div>
              <div><span className="text-muted-foreground">Błędów:</span> <strong>{scanState.last_history.errors}</strong></div>
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">
              {new Date(scanState.last_history.finished_at || scanState.last_history.started_at).toLocaleString("pl-PL")}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filtry statusów */}
      <div className="flex gap-2">
        {["pending_review", "applied", "rejected"].map((s) => (
          <button key={s} onClick={() => setStatus(s)}
                  className={`px-3 py-1.5 rounded text-xs font-medium border ${status === s ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
            {statusInfo[s].label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-muted-foreground">
          Brak wpisów w statusie <strong>{statusInfo[status]?.label}</strong>.
          {status === "pending_review" && " Uruchom skan aby wykryć problemy."}
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <Card key={it.id} className="cursor-pointer hover:border-amber-400 transition"
                  onClick={() => openDetail(it.id)}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{it.title || "(bez tytułu)"}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Portal: <strong>{it.site_name || "?"}</strong>
                      {it.published_url && <> · <a href={it.published_url} target="_blank" rel="noopener" className="text-blue-600 hover:underline inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>otwórz <ExternalLink className="w-3 h-3" /></a></>}
                    </div>
                    <div className="text-xs mt-2 flex flex-wrap gap-1">
                      <Badge variant="destructive" className="text-[10px]">
                        {it.dead_urls?.length || 0} dead link(ów)
                      </Badge>
                      <span className="text-[10px] text-muted-foreground">
                        {new Date(it.created_at).toLocaleString("pl-PL")}
                      </span>
                    </div>
                  </div>
                  <Button size="sm" variant="ghost"><Eye className="w-4 h-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal detail */}
      {selected && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
             onClick={() => setSelected(null)}>
          <div className="bg-background rounded-lg shadow-xl max-w-none w-full max-h-[90vh] overflow-y-auto"
               onClick={(e) => e.stopPropagation()}>
            <div className="p-5 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-bold">{selected.title}</h2>
                  <p className="text-xs text-muted-foreground">Portal: <strong>{selected.site_name}</strong> · brand: <strong>{selected.brand}</strong></p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelected(null)}><X className="w-4 h-4" /></Button>
              </div>

              {/* Lista linków ze statusami */}
              <div>
                <h3 className="text-sm font-semibold mb-2">Sprawdzone linki ({selected.links_checked?.length || 0})</h3>
                <div className="space-y-1 text-xs">
                  {selected.links_checked?.map((ln, i) => (
                    <div key={i} className={`p-2 rounded border ${ln.status === "ok" ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}>
                      <div className="flex items-center gap-2">
                        <Badge variant={ln.status === "ok" ? "default" : "destructive"} className="text-[10px]">{ln.status.toUpperCase()}</Badge>
                        <span className="font-mono text-[10px] truncate flex-1">{ln.href}</span>
                        <span className="text-muted-foreground">{ln.http_code || ""}</span>
                      </div>
                      {ln.error && <div className="text-[10px] text-red-700 mt-0.5">{ln.error}</div>}
                      {ln.host_changed && <div className="text-[10px] text-amber-700 mt-0.5">⚠ Host zmienił się na: {ln.final_url}</div>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Diff old vs new */}
              <div>
                <h3 className="text-sm font-semibold mb-2">Zmiany contentu (przed / po)</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs font-medium text-red-600 mb-1">Oryginalna wersja</div>
                    <div className="border rounded p-2 text-[11px] max-h-96 overflow-y-auto bg-red-50/30"
                         dangerouslySetInnerHTML={{ __html: selected.original_content || "(brak)" }} />
                  </div>
                  <div>
                    <div className="text-xs font-medium text-emerald-600 mb-1">Zaproponowana wersja</div>
                    <div className="border rounded p-2 text-[11px] max-h-96 overflow-y-auto bg-emerald-50/30"
                         dangerouslySetInnerHTML={{ __html: selected.suggested_new_content || "(brak)" }} />
                  </div>
                </div>
              </div>

              {selected.status === "pending_review" && (
                <div className="flex justify-end gap-2 pt-3 border-t">
                  <Button variant="destructive" onClick={rejectMod} disabled={processing}>
                    <X className="w-3.5 h-3.5 mr-1" /> Odrzuć
                  </Button>
                  <Button onClick={applyMod} disabled={processing} className="bg-emerald-600 hover:bg-emerald-700">
                    {processing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
                    Zatwierdź i zastosuj
                  </Button>
                </div>
              )}
              {selected.status !== "pending_review" && (
                <div className="text-xs text-muted-foreground border-t pt-3">
                  Status: <strong>{statusInfo[selected.status]?.label || selected.status}</strong>
                  {selected.applied_at && <> · zastosowano {new Date(selected.applied_at).toLocaleString("pl-PL")}</>}
                  {selected.rejected_at && <> · odrzucono {new Date(selected.rejected_at).toLocaleString("pl-PL")}</>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
